import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { FaTimes, FaEnvelope, FaLock, FaUser, FaPhone, FaEye, FaEyeSlash, FaCheckCircle } from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
  const { loginWithGoogle, login, register } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ name: '', email: '', password: '', phone: '' });

  if (!isOpen) return null;

  const handleClose = () => {
    setError('');
    setSuccess('');
    onClose();
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle(credentialResponse.credential);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginForm);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await register(regForm);
      setSuccess('Account created! Welcome to The Charcoal Grill 🎉');
      setTimeout(handleClose, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="auth-modal" id="auth-modal">
        {/* Header */}
        <div className="auth-header">
          <div className="auth-logo">
            <GiKnifeFork className="auth-logo-icon" />
            <div>
              <span className="auth-logo-name">The Charcoal Grill</span>
              <span className="auth-logo-sub">Cafe &amp; Bistro</span>
            </div>
          </div>
          <button className="auth-close-btn" onClick={handleClose} id="auth-close-btn"><FaTimes /></button>
        </div>

        {/* Tabs */}
        <div className="auth-tabs">
          <button
            className={`auth-tab ${tab === 'login' ? 'active' : ''}`}
            onClick={() => { setTab('login'); setError(''); }}
            id="auth-tab-login"
          >Sign In</button>
          <button
            className={`auth-tab ${tab === 'register' ? 'active' : ''}`}
            onClick={() => { setTab('register'); setError(''); }}
            id="auth-tab-register"
          >Create Account</button>
        </div>

        <div className="auth-body">
          {/* Success state */}
          {success ? (
            <div className="auth-success">
              <FaCheckCircle className="auth-success-icon" />
              <p>{success}</p>
            </div>
          ) : (
            <>
              {/* Google Button */}
              <div className="auth-google-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in was cancelled or failed.')}
                  theme="filled_black"
                  shape="rectangular"
                  width="100%"
                  text={tab === 'login' ? 'signin_with' : 'signup_with'}
                />
              </div>

              <div className="auth-divider">
                <span className="auth-divider-line" />
                <span className="auth-divider-text">or continue with email</span>
                <span className="auth-divider-line" />
              </div>

              {/* Error */}
              {error && <div className="auth-error">{error}</div>}

              {/* Login Form */}
              {tab === 'login' && (
                <form className="auth-form" onSubmit={handleLogin} id="login-form">
                  <div className="af-group">
                    <label className="af-label" htmlFor="login-email"><FaEnvelope /> Email</label>
                    <input id="login-email" type="email" className="af-input" placeholder="your@email.com"
                      value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} required />
                  </div>
                  <div className="af-group">
                    <label className="af-label" htmlFor="login-password"><FaLock /> Password</label>
                    <div className="af-input-wrap">
                      <input id="login-password" type={showPassword ? 'text' : 'password'} className="af-input"
                        placeholder="••••••••" value={loginForm.password}
                        onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} required />
                      <button type="button" className="af-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className={`btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
                    disabled={loading} id="login-submit-btn">
                    {loading ? <><span className="spinner" /><span>Signing in...</span></> : <span>Sign In</span>}
                  </button>
                  <p className="auth-switch">
                    Don't have an account?{' '}
                    <button type="button" className="auth-switch-link" onClick={() => setTab('register')}>
                      Create one
                    </button>
                  </p>
                </form>
              )}

              {/* Register Form */}
              {tab === 'register' && (
                <form className="auth-form" onSubmit={handleRegister} id="register-form">
                  <div className="af-group">
                    <label className="af-label" htmlFor="reg-name"><FaUser /> Full Name</label>
                    <input id="reg-name" type="text" className="af-input" placeholder="John Smith"
                      value={regForm.name} onChange={e => setRegForm({ ...regForm, name: e.target.value })} required />
                  </div>
                  <div className="af-group">
                    <label className="af-label" htmlFor="reg-email"><FaEnvelope /> Email</label>
                    <input id="reg-email" type="email" className="af-input" placeholder="your@email.com"
                      value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })} required />
                  </div>
                  <div className="af-group">
                    <label className="af-label" htmlFor="reg-phone"><FaPhone /> Phone (optional)</label>
                    <input id="reg-phone" type="tel" className="af-input" placeholder="+353 87 000 0000"
                      value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} />
                  </div>
                  <div className="af-group">
                    <label className="af-label" htmlFor="reg-password"><FaLock /> Password</label>
                    <div className="af-input-wrap">
                      <input id="reg-password" type={showPassword ? 'text' : 'password'} className="af-input"
                        placeholder="Min. 8 characters" value={regForm.password}
                        onChange={e => setRegForm({ ...regForm, password: e.target.value })} required />
                      <button type="button" className="af-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className={`btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
                    disabled={loading} id="register-submit-btn">
                    {loading ? <><span className="spinner" /><span>Creating account...</span></> : <span>Create Account</span>}
                  </button>
                  <p className="auth-switch">
                    Already have an account?{' '}
                    <button type="button" className="auth-switch-link" onClick={() => setTab('login')}>
                      Sign in
                    </button>
                  </p>
                </form>
              )}

              {/* Guest note */}
              <div className="auth-guest-note">
                <button className="auth-guest-btn" onClick={handleClose} id="auth-guest-btn">
                  Continue as Guest →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
